import { ErrorBoundary } from '@sentry/react';
import { ButtonIcon, Card, Status as StatusComponent } from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { currentPlayerMetricDistributionsAtom, selectedMetricsAtom } from '@/atoms/dataLocker/metrics';
import { isMetricFiltersModalOpenAtom } from '@/atoms/dataLocker/player/modal';
import { AGGREGATES_COLUMN_PREFIX } from '@/utils/metrics';
import { GeneralErrorBoundaryFallback } from '../errorBoundary/ErrorBoundaryFallback';
import { MetricFiltersModal } from '../modal/MetricFiltersModal/MetricFiltersModal';
import { MetricDistributionSlider } from '../sliders/MetricDistributionSlider';

const OpenMetricFiltersModalButton = () => {
  const { t } = useTranslation('filters');
  const setIsMetricFiltersModalOpen = useSetAtom(isMetricFiltersModalOpenAtom);

  return (
    <ButtonIcon
      icon="PlusAdd"
      size="small"
      variant="secondary"
      displayText="right"
      onClick={() => setIsMetricFiltersModalOpen(true)}
    >
      {t('addMetricFilter')}
    </ButtonIcon>
  );
};

const MetricFiltersStatus = () => {
  const { t } = useTranslation('filters');

  return (
    <div className="h-full flex items-center min-h-36">
      <div className="w-full">
        <Card>
          <StatusComponent type="info" title={t('metricInfo.title')}>
            {t('metricInfo.description')}
            <div className="flex justify-center mt-4">
              <OpenMetricFiltersModalButton />
            </div>
          </StatusComponent>
        </Card>
      </div>
    </div>
  );
};

const PlayerDataLockerMetricFiltersBase = () => {
  const playerMetricDistributions = useAtomValue(currentPlayerMetricDistributionsAtom);
  const playerDataLockerSelectedMetrics = useAtomValue(selectedMetricsAtom);
  const displayStatus = !playerDataLockerSelectedMetrics.length;
  // undefined means the distributions query has not resolved yet; an empty list means it resolved
  // with nothing, which is a genuine failure for every selected metric rather than a loading state
  const isLoadingDistributions = playerMetricDistributions === undefined;

  const findDistribution = (metric: string) =>
    playerMetricDistributions?.definitions.find(
      definition => `${AGGREGATES_COLUMN_PREFIX}${definition.metricKeyNormalised}` === metric,
    );

  // A metric without a distribution is only a gap once the definitions answer for the current selection: a newly
  // selected metric is missing from the held ones until its own request resolves.
  const missingMetricsKey = playerMetricDistributions?.isCurrent
    ? playerDataLockerSelectedMetrics.filter(metric => !findDistribution(metric)).join(',')
    : '';

  // Logged from an effect so it fires once per change in what is missing, not on every render.
  useEffect(() => {
    if (!missingMetricsKey) return;
    missingMetricsKey.split(',').forEach(metric => {
      // eslint-disable-next-line no-console
      console.error(`unable to find matching metric distribution for metric key: ${metric}`);
    });
  }, [missingMetricsKey]);

  return (
    <>
      {displayStatus ? (
        <MetricFiltersStatus />
      ) : (
        <div>
          <OpenMetricFiltersModalButton />
        </div>
      )}
      {!isLoadingDistributions &&
        playerDataLockerSelectedMetrics.map(metric => {
          const metricDistribution = findDistribution(metric);

          if (!metricDistribution) return null;

          const { percentiles, step, reverseScale, percentage, precision } = metricDistribution;

          return (
            <MetricDistributionSlider
              key={metric}
              metric={metric}
              range={[parseFloat(percentiles[5].formattedValue), parseFloat(percentiles[95].formattedValue)]}
              step={step}
              isPercentage={percentage}
              precision={precision}
              isReversed={reverseScale}
            />
          );
        })}
      <MetricFiltersModal />
    </>
  );
};

export const PlayerDataLockerMetricFilters = () => (
  <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
    <PlayerDataLockerMetricFiltersBase />
  </ErrorBoundary>
);
